import { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { useVideoPlaybackUrl } from './useTalentVideos';
import type { TalentVideoItem } from './talentVideosApi';

/**
 * Player de un video del talent. Diseño on-demand:
 *   - Pre-play: placeholder con título + botón play
 *   - Click play → pide signed URL → renderiza <video> con src + autoplay
 *   - Si la URL se acerca a expirar (-60s del expiresAt) y el user está mirando,
 *     re-pide la URL y actualiza el src; el browser hace seamless re-stream
 */
export function VideoPlayer({
  video,
  shortlistPublicKey,
  talentPublicKey,
}: {
  video: TalentVideoItem;
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const query = useVideoPlaybackUrl(
    { shortlistPublicKey, talentPublicKey, videoPublicKey: video.id },
    active,
  );

  // Auto-refresh del URL antes de expirar — restamos 60s de margen para evitar
  // race con el clock skew. Cuando refetch resuelve, react re-render con la nueva src.
  useEffect(() => {
    if (!query.isSuccess || !active) return;
    const expiresAtMs = new Date(query.data.expiresAt).getTime();
    const ttlMs = expiresAtMs - Date.now() - 60_000;
    if (ttlMs <= 0) {
      void query.refetch();
      return;
    }
    const timer = window.setTimeout(() => void query.refetch(), ttlMs);
    return () => window.clearTimeout(timer);
  }, [query, active]);

  if (!active) {
    return (
      <PlayPlaceholder video={video} onPlay={() => setActive(true)} />
    );
  }

  if (query.isPending) {
    return (
      <Box
        data-testid={`video-loading-${video.id}`}
        sx={loadingSx}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Alert severity="warning" data-testid={`video-error-${video.id}`}>
        {t('opportunities.talent.video.error')}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '14px',
        overflow: 'hidden',
        background: '#000',
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        data-testid={`video-element-${video.id}`}
        controls
        autoPlay
        playsInline
        src={query.data.url}
        sx={{ width: '100%', display: 'block', maxHeight: 480 }}
      >
        <Box component="source" src={query.data.url} type={query.data.mimeType} />
      </Box>
    </Box>
  );
}

function PlayPlaceholder({ video, onPlay }: { video: TalentVideoItem; onPlay: () => void }) {
  const { t } = useTranslation();
  const duration = video.durationSeconds ? formatDuration(video.durationSeconds) : null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onPlay}
      data-testid={`video-play-${video.id}`}
      sx={{
        width: '100%',
        cursor: 'pointer',
        background: tokens.color.surface2,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        textAlign: 'left',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': {
          borderColor: tokens.color.accent,
          boxShadow: '0 1px 0 rgba(15,15,15,.03), 0 6px 24px -16px rgba(15,15,15,.18)',
        },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: tokens.color.accent,
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          fontSize: 22,
          ml: '14px',
          my: '14px',
        }}
      >
        ▶
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, my: '14px' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: tokens.color.ink }}>
          {video.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 10.5,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: tokens.color.muted,
            mt: '4px',
          }}
        >
          {duration ?? t('opportunities.talent.video.play')}
        </Typography>
      </Box>
    </Box>
  );
}

const loadingSx = {
  width: '100%',
  height: 200,
  borderRadius: '14px',
  background: tokens.color.surface2,
  border: `1px solid ${tokens.color.line}`,
  display: 'grid',
  placeItems: 'center',
};

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
